import { useApps } from '@auth/useApps'
import {
  CursorResult,
  listTimelineItems,
  TimelineItem,
  TimelineItemsParams,
} from '@kudos/http-client'
import { AppPermission } from '@shared/permissions'
import { firstValue, SearchState } from '@shared/search/state'
import { TimelineItemTypeFilterField } from '@shared/timeline/types'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { awardEmbeds, recognitionEmbeds } from '@tenant/home/user/timeline/useTimelineItems'
import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/shallow'
import { useMemberTimelineFiltersStore } from './useMemberTimelineFiltersStore'

export const MEMBER_TIMELINE_ITEMS_QK = 'memberTimelineItemsQueryKey'

export const useMemberTimelineItems = (tenantMemberId: string) => {
  const [hasAppPermission] = useApps()
  const canBrowseRecognitions = hasAppPermission(
    'recognitionApp',
    AppPermission.RecognitionAppBrowse,
  )
  const queryClient = useQueryClient()
  const { memberTimelineFilters, setMemberTimelineFilters } = useMemberTimelineFiltersStore(
    useShallow(state => ({
      memberTimelineFilters: state.memberTimelineFilters,
      setMemberTimelineFilters: state.setMemberTimelineFilters,
    })),
  )
  const [filters, setFilters] =
    useState<SearchState<TimelineItemTypeFilterField>>(memberTimelineFilters)

  const parseMyTimelineFilters = (
    filters: SearchState<TimelineItemTypeFilterField>,
  ): TimelineItemsParams => {
    const itemTypeIn =
      filters.itemTypeIn.length === 0 ? ['award', 'recognition'] : filters.itemTypeIn

    return { itemTypeIn, sentOrReceivedById: tenantMemberId, includePinned: true }
  }

  useEffect(() => {
    if (!canBrowseRecognitions && firstValue(memberTimelineFilters.itemTypeIn) === 'recognition') {
      setFilters({
        itemTypeIn: [],
      })
    }
  }, [canBrowseRecognitions, memberTimelineFilters])

  const resetInfiniteQueryPagination = (): void => {
    queryClient.setQueryData(
      [MEMBER_TIMELINE_ITEMS_QK],
      (oldData: {
        pageParams: { pageParam: null | string }[]
        pages: CursorResult<TimelineItem>[]
      }) => {
        if (!oldData) return undefined

        return {
          pages: oldData.pages.slice(0, 1),
          pageParams: oldData.pageParams.slice(0, 1),
        }
      },
    )
  }

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, isLoading, isFetching } =
    useInfiniteQuery({
      queryKey: [MEMBER_TIMELINE_ITEMS_QK],
      queryFn: ({ pageParam = null }: { pageParam: null | string }) =>
        listTimelineItems({
          cursor: pageParam,
          recognitionEmbeds: [...recognitionEmbeds, 'ownRecipient'],
          awardEmbeds: [...awardEmbeds, 'ownRecipient'],
          ...parseMyTimelineFilters(filters),
        }),
      staleTime: 1 * 60 * 1000,
      getNextPageParam: lastPage => lastPage.next,
      initialPageParam: null,
    })

  useEffect(() => {
    setMemberTimelineFilters(filters)
    resetInfiniteQueryPagination()
    refetch()
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.itemTypeIn])

  return {
    timelineData: data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    filters,
    setFilters,
    isLoading: isLoading || isFetching,
    canBrowseRecognitions,
  }
}
